import React from 'react';
import { useState, useEffect } from 'react';
import { ProSidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Box, IconButton, Typography, useTheme, useMediaQuery } from '@mui/material';
import "react-pro-sidebar/dist/css/styles.css";
import { Link } from 'react-router-dom';
import { tokens } from '../../../theme';
import useAuth from '../../../hooks/useAuth';
import {
  HomeOutlined,
  PeopleOutlined,
  MenuOutlined,
  CorporateFareOutlined,
  BusinessOutlined,
  DevicesOutlined,
  BuildOutlined,
  StorefrontOutlined,
  LocalShippingOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import { usfuel } from '../../../assets';
import { useGetOrganizationByIdQuery } from '../../../features/organizations/organizationsApiSlice';

const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const isMobile = useMediaQuery('(max-width:600px)'); // Media query for small screens
  const { username, status, organization_id } = useAuth();

  const {
    data: org_query,
    isLoading: isOrgLoading,
  } = useGetOrganizationByIdQuery(organization_id);

  const organization =
    org_query?.entities?.[
    organization_id
    ];


  const isAdmin =
    status === 'Admin' ||
    organization_id === 'admin';


  const canManageOrganizations =
    isAdmin ||
    organization?.type ===
    'Compliance';

  const [isCollapsed, setIsCollapsed] = useState(isMobile); // Default state based on screen size
  const [selected, setSelected] = useState('Dashboard');

  useEffect(() => {
    setIsCollapsed(isMobile); // Update state when screen size changes
  }, [isMobile]);

  return (
    <Box
      display="flex"
      height="100%"
      sx={{
        '& .pro-sidebar-inner': {
          background: `${colors.primary[400]} !important`,
        },
        '& .pro-icon-wrapper': {
          backgroundColor: 'transparent !important',
        },
        '& .pro-inner-item': {
          padding: '5px 35px 5px 20px !important',
        },
        '& .pro-inner-item:hover': {
          color: '#868dfb !important',
        },
        '& .pro-menu-item.active': {
          color: '#6870fa !important',
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: isCollapsed ? 0 : 2,
              mt: 1.5,
              mb: 2.5,
              minHeight: '100px',
            }}
          >
            {isCollapsed ? (
              <IconButton
                onClick={() =>
                  setIsCollapsed(false)
                }
                sx={{
                  color: colors.grey[100],
                }}
              >
                <MenuOutlined />
              </IconButton>
            ) : (
              <>
                {/* LOGO -> PUBLIC HOMEPAGE */}

                <Link
                  to="/"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    width: '100%',
                    transform: 'translateX(-12px)',
                  }}
                >
                  <img
                    alt="USFuel"
                    src={usfuel}
                    style={{
                      width: '170px',
                      maxWidth: '72%',
                      height: '90px',
                      objectFit: 'contain',
                      cursor: 'pointer',
                    }}
                  />
                </Link>


                {/* HAMBURGER -> COLLAPSE SIDEBAR */}

                <IconButton
                  onClick={() =>
                    setIsCollapsed(true)
                  }
                  sx={{
                    position: 'absolute',
                    right: 0,
                    color: colors.grey[100],
                  }}
                >
                  <MenuOutlined />
                </IconButton>
              </>
            )}
          </Box>

          {!isCollapsed && (
            <Box mb="25px">
              <Box textAlign="center">
                <Typography
                  color={colors.grey[100]}
                  fontWeight={700}
                  sx={{
                    mt: 1,
                    px: 2,
                    fontSize: '0.95rem',
                    lineHeight: 1.35,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    textAlign: 'center',
                  }}
                >
                  {username}
                </Typography>
                <Typography
                  variant="h6"
                  color={colors.greenAccent[500]}
                  sx={{
                    mt: 0.75,
                    px: 2,
                    fontWeight: 700,
                  }}
                >
                  ATG Console {status}
                </Typography>
              </Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : '10%'}>
            <Item
              title="Dashboard"
              to="/console"
              icon={<HomeOutlined />}
              selected={selected}
              setSelected={setSelected}
            />

            <Typography
              variant="h6"
              color={colors.grey[300]}
              sx={{ m: '15px 0 5px 20px' }}
            >
              Manage
            </Typography>
            {canManageOrganizations && (
              <Item
                title="My Organizations"
                to="/console/organizations"
                icon={<CorporateFareOutlined />}
                selected={selected}
                setSelected={setSelected}
              />
            )}
            {status === 'Manager' && (
              <Item
                title="My Organization"
                to={`/console/organization/${organization_id}`}
                icon={<BusinessOutlined />}
                selected={selected}
                setSelected={setSelected}
              />
            )}
            {(status === 'Admin' || status === 'Manager') && (
              <Item
                title="Manage Team"
                to="/console/users"
                icon={<PeopleOutlined />}
                selected={selected}
                setSelected={setSelected}
              />
            )}
            {status === 'Admin' && (
              <Item
                title="US Fuel Techs"
                to="/console/techs"
                icon={<BuildOutlined />}
                selected={selected}
                setSelected={setSelected}
              />
            )}
            <Item
              title="Manage ATGs"
              to="/console/atgs"
              icon={<DevicesOutlined />}
              selected={selected}
              setSelected={setSelected}
            />
            {/* ORDERS */}

            <Typography
              variant="h6"
              color={colors.grey[300]}
              sx={{
                m:
                  '25px 0 5px 20px',
              }}
            >
              Orders
            </Typography>


            {status === 'Admin' ? (
              <Item
                title="Manage Orders"
                to="/console/orders"
                icon={
                  <LocalShippingOutlined />
                }
                selected={
                  selected
                }
                setSelected={
                  setSelected
                }
              />
            ) : (
              <Item
                title="My Orders"
                to="/orders"
                icon={
                  <ReceiptLongOutlined />
                }
                selected={
                  selected
                }
                setSelected={
                  setSelected
                }
              />
            )}


            {/* STORE ADMINISTRATION */}

            {status === 'Admin' && (
              <>
                <Typography
                  variant="h6"
                  color={
                    colors.grey[300]
                  }
                  sx={{
                    m:
                      '25px 0 5px 20px',
                  }}
                >
                  Store
                </Typography>

                <Item
                  title="Store Management"
                  to="/console/store-management"
                  icon={
                    <StorefrontOutlined />
                  }
                  selected={
                    selected
                  }
                  setSelected={
                    setSelected
                  }
                />
              </>
            )}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
